// Date helpers shared across calendar-style screens.
//
// These were duplicated verbatim in fieldtech/FieldTechSchedule.js and
// fieldtech/FieldTechRoster.js; the portal calendar is the third caller, so
// they live here now. The two fieldtech screens still carry their own copies of
// formatDate — migrating those is a separate job. getWeekDates has been pulled
// up, because the tech dashboard is now a second caller.

// "YYYY-MM-DD" in LOCAL time. Never use toISOString() for this: it converts to
// UTC first, so anything east of Greenwich loses a day near midnight.
export function formatDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// The other half of the same trap: "2026-10-20" parsed bare is UTC midnight,
// which renders as the 19th here. Anchoring the time keeps the day intact.
export function parseDay(value) {
  return value ? new Date(value + "T00:00:00") : null;
}

// Monday to Friday of a week, as Date objects. weekOffset 0 is the current
// week, 1 the next, -1 the last.
//
// Sunday counts as the END of its week, not the start: dow 0 walks back six
// days rather than forward one. A tech looking at this on Sunday night is
// looking at the week that just finished, so a caller that means "the week
// ahead" has to ask for it.
export function getWeekDates(weekOffset = 0) {
  const today = new Date();
  const dow = today.getDay();
  const monday = new Date(today);
  monday.setDate(today.getDate() - (dow === 0 ? 6 : dow - 1) + weekOffset * 7);
  return Array.from({ length: 5 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
}

export function isPast(date) {
  if (!date) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
}

export function isToday(date) {
  if (!date) return false;
  return formatDate(date) === formatDate(new Date());
}

export function isWeekend(date) {
  if (!date) return false;
  const d = date.getDay();
  return d === 0 || d === 6;
}

// Monday-first grid, leading blanks as null, padded to a whole number of weeks.
export function getMonthGrid(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startDow = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
  const grid = [];
  for (let i = 0; i < startDow; i++) grid.push(null);
  for (let d = 1; d <= lastDay.getDate(); d++) grid.push(new Date(year, month, d));
  while (grid.length % 7 !== 0) grid.push(null);
  return { grid, year: firstDay.getFullYear(), month: firstDay.getMonth() };
}

// Same grid, addressed as an offset from this month — the shape the fieldtech
// screens use.
export function getMonthGridByOffset(monthOffset) {
  const today = new Date();
  return getMonthGrid(today.getFullYear(), today.getMonth() + monthOffset);
}

export function eachDay(from, to) {
  const out = [];
  const d = parseDay(from);
  const end = parseDay(to);
  while (d && end && d <= end) {
    out.push(formatDate(d));
    d.setDate(d.getDate() + 1);
  }
  return out;
}

// "Mon 20 Oct" — the house format for a day label.
export function formatDayLabel(value, opts = {}) {
  const d = parseDay(value);
  if (!d) return "";
  return d.toLocaleDateString("en-AU", {
    weekday: opts.weekday === false ? undefined : "short",
    day: "2-digit",
    month: "short",
    ...(opts.year ? { year: "numeric" } : {}),
  });
}

// "20 Oct" — no weekday, for tight columns.
export function formatShortDate(value) {
  const d = parseDay(value);
  if (!d) return "";
  return d.toLocaleDateString("en-AU", { day: "2-digit", month: "short" });
}

export const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
